// src/services/api.js
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

console.log('API URL configured:', API_URL);

// Helper function to handle rate limit errors
const handleRateLimit = (error) => {
  if (error.response?.status === 429) {
    const retryAfter = error.response.data?.retryAfter || 60;
    const message = error.response.data?.message || 
      `Too many requests. Please wait ${retryAfter} seconds before trying again.`;
    
    // Create a custom error that components can handle
    const rateLimitError = new Error(message);
    rateLimitError.isRateLimit = true;
    rateLimitError.retryAfter = retryAfter;
    rateLimitError.status = 429;
    throw rateLimitError;
  }
  throw error;
};

// Helper function to check response and handle errors
const checkResponse = async (response) => {
  if (response.status === 429) {
    const data = await response.json();
    const error = new Error(data.message || 'Too many requests');
    error.response = { status: 429, data };
    error.isRateLimit = true;
    error.retryAfter = data.retryAfter;
    throw error;
  }
  
  if (!response.ok && response.status !== 429) {
    const errorData = await response.json().catch(() => ({}));
    const error = new Error(errorData.message || errorData.error || `Server error: ${response.status}`);
    error.response = { status: response.status, data: errorData };
    throw error;
  }
  
  return response;
};

const api = {
  // Search results endpoint
  searchResults: async (data) => {
    try {
      const response = await fetch(`${API_URL}/api/results`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      
      await checkResponse(response);
      return response;
    } catch (error) {
      return handleRateLimit(error);
    }
  },

  // Resource count endpoint
  getResourceCount: async (zip) => {
    try {
      const response = await fetch(`${API_URL}/api/resources/count?zip=${zip}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      
      await checkResponse(response);
      return response;
    } catch (error) {
      return handleRateLimit(error);
    }
  },

  // City search endpoint
  searchCities: async (state, search) => {
    try {
      const response = await fetch(`${API_URL}/api/cities?state=${state}&search=${encodeURIComponent(search)}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      
      await checkResponse(response);
      return response;
    } catch (error) {
      return handleRateLimit(error);
    }
  },

  // Compare city endpoint
  compareCity: async (data) => {
    try {
      const response = await fetch(`${API_URL}/api/compare-city`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      
      await checkResponse(response);
      return response;
    } catch (error) {
      return handleRateLimit(error);
    }
  }
};

// Export the api object as default
export default api;

// Also export the handleRateLimit function so components can use it directly if needed
export { handleRateLimit };